import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import dayjs from "dayjs";

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const sum = (values: number[] = []) =>
  !values.length ? 0 : values.reduce((a, b) => a + b);

const toCurrency = (value: number | string) =>
  `Rs. ${Number(Number(value || 0).toFixed(2))}`;

const phoneDigits = (phone = "") =>
  String(phone).replace(/\D/g, "").replace(/^92/, "0");

const samePhone = (a?: string, b?: string) =>
  !!a && !!b && phoneDigits(a) === phoneDigits(b);

const parseAmount = (value: any): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const match = String(value ?? "")
    .replace(/,/g, "")
    .match(/(\d+(\.\d+)?)/);
  return match ? Number(match[1]) : 0;
};

const uuid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const mediaUrl = (url?: string) => {
  if (!url) return "";
  if (
    /^https?:\/\//i.test(url) ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  )
    return url;

  const base = (process.env.REACT_APP_R2_PUBLIC_URL || "").replace(/\/$/, "");
  const path = url.replace(/^\//, "");
  return base ? `${base}/${path}` : `/${path}`;
};

/** Absolute http(s) as-is; relative / query-only links against the current origin. */
const resolveLink = (link?: string | null) => {
  if (!link) return "";
  if (/^https?:\/\//i.test(link)) return link;
  try {
    return new URL(link, window.location.origin).href;
  } catch {
    return link;
  }
};

const storage = {
  ...localStorage,
  clear: () => {
    localStorage.clear();
  },
  removeItem: (id: string) => localStorage.removeItem(id),
  setItem: (id: string, value: any) =>
    localStorage.setItem(id, JSON.stringify(value)),
  getItem: (id: string) => {
    const data = localStorage.getItem(id);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch (error) {
      return null;
    }
  },
};

const initials = (value: string) =>
  !value
    ? ""
    : value
        .split(" ")
        .map((_) => _[0].toUpperCase())
        .join("");

const toPascalCase = (value?: string) =>
  !value
    ? ""
    : value
        .split(" ")
        .map((_) => `${_[0].toUpperCase()}${_.slice(1)}`)
        .join(" ");

const getCssVar = (name = "") =>
  getComputedStyle(document.documentElement)
    .getPropertyValue(`--${name}`)
    .trim();

const date = dayjs;

const parseClock = (time: string) => {
  const formats = [
    "HH:mm",
    "H:mm",
    "HH:mm:ss",
    "hh:mm A",
    "h:mm A",
    "hh:mm a",
    "h:mm a",
  ];
  for (const format of formats) {
    const parsed = date(time, format);
    if (parsed.isValid()) return parsed;
  }
  return date(time);
};

const isBetweenTime = (open: string, close: string, now = date()) => {
  let start = parseClock(open);
  let end = parseClock(close);
  if (!start.isValid() || !end.isValid()) return true;

  // Overnight window (e.g. 12:00 → 04:00): 1:29 AM is still open.
  if (!end.isAfter(start)) {
    if (!now.isAfter(end, "minute")) start = start.subtract(1, "day");
    else end = end.add(1, "day");
  }

  return now.isBetween(start, end, "minute", "[]");
};

const formatTime = (time: string | Date, format: "12" | "24" = "12") =>
  date(time, typeof time === "string" ? "HH:mm" : undefined).format(
    format === "12" ? "hh:mm A" : "hh:mm",
  );

const asText = (value: any): string => {
  if (value == null || value === false) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return asText(
      value.name ??
        value.title ??
        value.label ??
        value.value ??
        value.phone ??
        value.url ??
        value.description ??
        "",
    );
  }
  return "";
};

export default {
  sum,
  date,
  uuid,
  storage,
  initials,
  mediaUrl,
  resolveLink,
  asText,
  getCssVar,
  formatTime,
  toCurrency,
  parseAmount,
  samePhone,
  toPascalCase,
  isBetweenTime,
};
